/** Cuota diaria de consultas al detalle catastral. @see docs/maps/lot-view-quota.md */

import { formatDateTimeRelativeAR } from '../../Utils/dateTimeFormat';

export const LOT_VIEW_QUOTA_EXCEEDED_MESSAGE =
  'Alcanzaste tu límite diario de consultas.';

/**
 * Este lote ya tiene detalle consultado: UUID, coordenadas y secciones no se recortan
 * aunque el cupo diario esté lleno. `already_viewed` es del lote; `hasViewedLotDetails`
 * cubre el dock cuando el flag no llega (p. ej. apertura desde listas).
 *
 * @param {object|null|undefined} quota
 * @param {{ hasViewedLotDetails?: boolean, isQuotaExceeded?: boolean }} [options]
 * @returns {boolean}
 */
export function isLotPreviouslyViewedForQuota(quota, {
  hasViewedLotDetails = false,
  isQuotaExceeded = false,
} = {}) {
  if (quota?.alreadyViewed === true) return true;
  const remaining = Number(quota?.remaining);
  if (!isQuotaExceeded && Number.isFinite(remaining) && remaining === 0) return true;
  if (isQuotaExceeded && hasViewedLotDetails) return true;
  return false;
}

/**
 * Aviso de cuota agotada, con horario de renovación si el backend lo mandó.
 * @param {string|Date|null|undefined} resetsAt
 * @returns {string}
 */
export function buildLotViewQuotaExceededMessage(resetsAt) {
  const relative = formatDateTimeRelativeAR(resetsAt);
  if (relative) return `Alcanzaste tu límite diario. Se renueva ${relative}.`;
  return LOT_VIEW_QUOTA_EXCEEDED_MESSAGE;
}

/**
 * Headers `X-Quota-*` de `/getalllots`.
 *
 * @param {Headers|null|undefined} headers
 * @returns {object|null}
 */
export function buildQuotaInfoFromHeaders(headers) {
  if (!headers || typeof headers.get !== 'function') return null;

  const limitRaw = headers.get('X-Quota-Limit');
  const usedRaw = headers.get('X-Quota-Used');
  const remainingRaw = headers.get('X-Quota-Remaining');
  const resetsAtRaw = headers.get('X-Quota-Resets-At');
  const exceededRaw = headers.get('X-Quota-Exceeded');

  const limit = limitRaw != null ? Number(limitRaw) : null;
  const used = usedRaw != null ? Number(usedRaw) : null;
  const remaining = remainingRaw != null ? Number(remainingRaw) : null;
  const resetsAt = resetsAtRaw ? String(resetsAtRaw) : null;
  const exceeded = exceededRaw != null ? String(exceededRaw).toLowerCase() === 'true' : false;

  const hasAny =
    limitRaw != null ||
    usedRaw != null ||
    remainingRaw != null ||
    resetsAtRaw != null ||
    exceededRaw != null;

  if (!hasAny) return null;

  return {
    limit: Number.isFinite(limit) ? limit : null,
    used: Number.isFinite(used) ? used : null,
    remaining: Number.isFinite(remaining) ? remaining : null,
    resetsAt,
    exceeded,
  };
}

/**
 * Objeto `quota` del body de `/getalllots`.
 *
 * @param {object|null|undefined} quota
 * @returns {object|null}
 */
export function buildQuotaInfoFromBody(quota) {
  if (!quota || typeof quota !== 'object') return null;

  const unlimited = quota.unlimited === true;
  const limit = quota.limit != null ? Number(quota.limit) : null;
  const used = quota.used != null ? Number(quota.used) : null;
  const remaining = quota.remaining != null ? Number(quota.remaining) : null;
  const resetsAt = quota.resets_at != null ? String(quota.resets_at) : null;
  const alreadyViewed = quota.already_viewed === true || quota.alreadyViewed === true;
  const exceeded = unlimited ? false : quota.exceeded === true;

  const hasAny =
    unlimited ||
    alreadyViewed ||
    quota.limit != null ||
    quota.used != null ||
    quota.remaining != null ||
    quota.resets_at != null ||
    quota.exceeded != null;

  if (!hasAny) return null;

  return {
    unlimited,
    limit: Number.isFinite(limit) ? limit : null,
    used: Number.isFinite(used) ? used : null,
    remaining: Number.isFinite(remaining) ? remaining : null,
    resetsAt,
    exceeded,
    alreadyViewed,
  };
}

/**
 * Combina body (prioridad) y headers.
 *
 * @param {object|null} primary
 * @param {object|null} fallback
 * @returns {object|null}
 */
export function mergeQuotaInfo(primary, fallback) {
  if (!primary && !fallback) return null;
  const p = primary || {};
  const f = fallback || {};
  const unlimited = (p.unlimited ?? f.unlimited) === true;
  return {
    unlimited,
    limit: p.limit ?? f.limit ?? null,
    used: p.used ?? f.used ?? null,
    remaining: p.remaining ?? f.remaining ?? null,
    resetsAt: p.resetsAt ?? f.resetsAt ?? null,
    exceeded: unlimited ? false : (p.exceeded ?? f.exceeded) === true,
    alreadyViewed: (p.alreadyViewed ?? f.alreadyViewed) === true,
  };
}

/**
 * ¿El objeto parece el body de cuota (no un lote)?
 * @param {object|null|undefined} value
 * @returns {boolean}
 */
const isQuotaBodyShape = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return value.exceeded != null
    || value.unlimited != null
    || value.resets_at != null
    || value.already_viewed != null
    || (value.limit != null && (value.used != null || value.remaining != null));
};

/**
 * Lee la cuota de un envelope de `httpClient`, aunque no haya sido OK.
 * Cubre `quota`, `data.quota`, `data` (GET /getlotviewquota) y headers.
 *
 * @param {object|null|undefined} result
 * @returns {object|null}
 */
export function parseLotViewQuotaFromHttpResult(result) {
  if (!result) return null;
  const nested = result.data && typeof result.data === 'object' ? result.data : null;
  const body = result.quota
    || nested?.quota
    || (isQuotaBodyShape(nested) ? nested : null);
  return mergeQuotaInfo(
    buildQuotaInfoFromBody(body),
    buildQuotaInfoFromHeaders(result._httpHeaders),
  );
}

/**
 * @param {object|null|undefined} result
 * @returns {object|null}
 */
export function parseLotViewQuotaFromResult(result) {
  return parseLotViewQuotaFromHttpResult(result);
}

/**
 * @param {object|null|undefined} result
 * @returns {object|null}
 */
export function parseLotViewQuotaFromStatusResult(result) {
  return parseLotViewQuotaFromHttpResult(result);
}

/**
 * 403 de cupo diario (`/getalllots`, parse, filtro oficial).
 *
 * @param {object|null|undefined} result
 * @returns {boolean}
 */
export function isLotViewQuotaForbiddenResult(result) {
  if (!result || result._httpOk || result._httpStatus !== 403) return false;
  return isLotViewQuotaExceeded(parseLotViewQuotaFromHttpResult(result));
}

/**
 * Respuesta de `fetchLotById` cuando el GET fue 403 de cupo (sin `data`).
 *
 * @param {object|null|undefined} value
 * @returns {boolean}
 */
export function isLotViewQuotaBlockedPayload(value) {
  return Boolean(value && value.lotViewQuotaBlocked === true);
}

/**
 * Límite diario agotado para filtros y búsqueda.
 *
 * @param {object|null|undefined} quota
 * @returns {boolean}
 */
export function isLotViewQuotaExceeded(quota) {
  if (!quota || quota.unlimited === true) return false;
  if (quota.exceeded === true) return true;
  const used = Number(quota.used);
  const limit = Number(quota.limit);
  if (Number.isFinite(used) && Number.isFinite(limit) && limit > 0 && used >= limit) {
    return true;
  }
  return false;
}

const toDailyLotViewQuota = (quota) => {
  if (!quota || typeof quota !== 'object') return quota;
  if (!Object.prototype.hasOwnProperty.call(quota, 'alreadyViewed')) return quota;
  const daily = { ...quota };
  delete daily.alreadyViewed;
  return daily;
};

/**
 * Aplica la cuota de un GET/POST al snapshot diario.
 * `already_viewed` es del lote, no del cupo: no se guarda en el lock del header.
 *
 * @param {object|null|undefined} prev
 * @param {object|null|undefined} next
 * @returns {object|null|undefined}
 */
export function mergeLotViewQuotaFromLotResponse(prev, next) {
  if (!next) return prev || next;
  return toDailyLotViewQuota(next);
}
